// BILLING-PLANS (docs/design/2026-10-27-wallet-pricing.md §3.5–§3.7, §6, §7, §10 phases 2–3):
// money that is not credit. Three surfaces:
//
// - the client's (Setup › Company › Billing, the Plan side), `/v1/companies/{co}/billing…`:
//   owners, admins and an agency operator (reading) see the plan, the agents and their tiers, the
//   next invoice, invoices with Pay and PDF links, quotes and build projects. Only an owner or an
//   admin opens the Stripe portal (card, bank account), accepts a quote or asks for credit by
//   invoice; an operator never pays with a client's money. Members and client viewers get 404.
// - the operator's price lists (Operator › Billing › Plans), `/v1/operator/billing/…`: platform
//   plan and maintenance tier versions, the charge catalog, the defaults and each template's tier
//   and setup fee. Reading needs an agency user; writing a platform admin (the operator).
// - the operator's company billing (Operator › Billing › Companies, Invoices & quotes),
//   `/v1/operator/billing/companies/{company_id}/…`: an agency operator of the company assigns the
//   plan and tiers, sets card or invoice collection, grants a founding-client discount, and
//   creates, sends, voids or marks paid invoices and quotes, and runs build projects.
//
// Stripe events arrive on `POST /v1/stripe/webhook` (wallet.ts). Money is integer cents.
import { z } from "zod";
import {
  BillingDefaults,
  BuildMilestone,
  BuildProject,
  ChargeCatalogItem,
  CompanyDiscount,
  DraftLine,
  Invoice,
  MaintenanceOverrides,
  MaintenancePlan,
  PlatformPlan,
  Quote,
  ScheduledChanges,
  UpcomingInvoice,
} from "../billing-rows";
import {
  BillingInterval,
  BuildMaintenanceShape,
  BuildProjectKind,
  BuildProjectStatus,
  ChargeKind,
  CollectionMethod,
  Currency,
  DigitalEmployeeStatus,
  DiscountAppliesTo,
  InvoicePaymentMethod,
  MaintenanceSupervision,
  MaintenanceTier,
  SubscriptionStatus,
  SystemUsageBilling,
} from "../enums";
import { IsoDate, MicrosAmount, MinorAmount, Slug, Timestamp, Uuid } from "../primitives";
import { CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const op = "/v1/operator/billing";
const billing = { feature: "core", tag: "billing" } as const;

const Cents = MinorAmount.min(0).max(10_000_000_000);
const Bp = z.int().min(1).max(10_000);

// Shared views ----------------------------------------------------------------------------------

/** The derived status the screens show: Open past its due date is Overdue. */
export const InvoiceDisplayStatus = z
  .enum(["draft", "open", "overdue", "paid", "void", "uncollectible"])
  .meta({ id: "InvoiceDisplayStatus" });

/** An invoice for a screen: the mirrored row plus its derived status and grace date. */
export const InvoiceView = Invoice.extend({
  display_status: InvoiceDisplayStatus,
  /** For an overdue subscription invoice: the day the company is suspended unless it is paid. */
  grace_until: Timestamp.nullable(),
}).meta({ id: "InvoiceView" });
export type InvoiceView = z.infer<typeof InvoiceView>;

/** The plan version a company is on, with the prices in force for it (its overrides applied). */
export const PlanTerms = z
  .object({
    key: z.string(),
    name: z.string(),
    version: z.int(),
    interval: BillingInterval,
    currency: Currency,
    base_price_minor: MinorAmount,
    included_seats: z.int(),
    extra_seat_price_minor: MinorAmount,
    grace_days: z.int(),
    /** True when a per-company override changed any figure above. */
    custom: z.boolean(),
  })
  .meta({ id: "PlanTerms" });

/** A discount and whether it applies today. */
export const DiscountView = CompanyDiscount.extend({ live: z.boolean() }).meta({
  id: "DiscountView",
});

export const SubscriptionView = z
  .object({
    id: Uuid,
    status: SubscriptionStatus,
    plan: PlanTerms,
    people_qty: z.int(),
    extra_seat_qty: z.int(),
    current_period_start: Timestamp.nullable(),
    current_period_end: Timestamp.nullable(),
    trial_end: Timestamp.nullable(),
    past_due_since: Timestamp.nullable(),
    grace_until: Timestamp.nullable(),
    suspended_at: Timestamp.nullable(),
    cancel_at_period_end: z.boolean(),
    /** The discount live today (or next to start), if any. */
    discount: DiscountView.nullable(),
    /** The next invoice as Stripe last previewed it; null until the first preview. */
    upcoming: UpcomingInvoice.nullable(),
    /** Decreases that take effect at the next renewal (never credited mid-period). */
    scheduled: ScheduledChanges.nullable(),
  })
  .meta({ id: "SubscriptionView" });

/** How the company pays, with the platform defaults filled in. */
export const BillingAccountView = z
  .object({
    collection_method: CollectionMethod,
    net_terms_days: z.int(),
    payment_methods: z.array(InvoicePaymentMethod),
    billing_email: z.string().nullable(),
    card_brand: z.string().nullable(),
    card_last4: z.string().nullable(),
    invoice_topups_allowed: z.boolean(),
  })
  .meta({ id: "BillingAccountView" });

/** One DE's maintenance line: its tier, price, AGs against the cap; AGs ride free. */
export const BillingAgentLine = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    status: DigitalEmployeeStatus,
    tier: MaintenanceTier.nullable(),
    tier_name: z.string().nullable(),
    monthly_minor: MinorAmount.nullable(),
    ag_count: z.int(),
    ag_cap: z.int().nullable(),
    since: IsoDate.nullable(),
    agents: z.array(z.object({ id: Uuid, name: z.string() })),
    /** A downgrade waiting for the renewal: the tier it moves to, and when. */
    scheduled_tier: MaintenanceTier.nullable(),
    scheduled_at: Timestamp.nullable(),
  })
  .meta({ id: "BillingAgentLine" });

/** The Plan side of the client's Billing page. */
export const BillingOverview = z
  .object({
    account: BillingAccountView,
    subscription: SubscriptionView.nullable(),
    agents: z.array(BillingAgentLine),
    /** The caller may pay, accept a quote or change the card (owner or admin, not an operator). */
    can_manage: z.boolean(),
    /** False until Stripe is configured (`STRIPE_SECRET_KEY`): the page says so. */
    stripe_ready: z.boolean(),
    /** Billing changes saved but not yet confirmed by Stripe (the page shows "Updating"). */
    pending_changes: z.int().min(0),
  })
  .meta({ id: "BillingOverview" });

export const ProjectView = z
  .object({
    project: BuildProject,
    milestones: z.array(BuildMilestone),
    invoiced_minor: MinorAmount,
    paid_minor: MinorAmount,
    /** The project's quote invoice that billed it in full; its milestones are then not billed. */
    settled_by_invoice_id: Uuid.nullable(),
  })
  .meta({ id: "ProjectView" });

// Client inputs --------------------------------------------------------------------------------

export const InvoiceListQuery = PageQuery.extend({
  status: InvoiceDisplayStatus.optional(),
});

const InvoiceParams = z.strictObject({ co: Uuid, invoice_id: Uuid });
const QuoteParams = z.strictObject({ co: Uuid, quote_id: Uuid });

export const PortalSession = z.object({ portal_url: z.url() }).meta({ id: "PortalSession" });

export const PortalCreate = z
  .strictObject({ return_url: z.url().max(2000).optional() })
  .meta({ id: "PortalCreate" });

export const TopupInvoiceCreate = z
  .strictObject({
    /** Credit before the bonus, micros of USD; the wallet's min / max apply. */
    amount_micros: MicrosAmount.min(1),
  })
  .meta({ id: "TopupInvoiceCreate" });

// Operator: price lists ---------------------------------------------------------------------------

export const PlatformPlanRow = PlatformPlan.extend({
  /** Companies whose subscription is on this version. */
  companies: z.int(),
  /** The version a new subscription gets today. */
  current: z.boolean(),
  /**
   * MODEL-KEYS-PLANS: companies on this plan (its key, every version) may bring their own model
   * keys (`cp.plan_model_access`; false when unset).
   */
  byo_model_keys: z.boolean(),
}).meta({ id: "PlatformPlanRow" });

/** MODEL-KEYS-PLANS: the per-plan "bring your own model keys" setting, set by plan key. */
export const PlanModelAccessPut = z
  .strictObject({ byo_model_keys: z.boolean() })
  .meta({ id: "PlanModelAccessPut" });
export const PlanModelAccess = z
  .object({
    plan_key: z.string(),
    byo_model_keys: z.boolean(),
    updated_by_user_id: Uuid.nullable(),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "PlanModelAccess" });
export type PlanModelAccess = z.infer<typeof PlanModelAccess>;
const PlanKeyParams = z.strictObject({ plan_key: Slug });

/** MODEL-KEYS-PLANS: one alias's provider target, "<provider>/<provider model id>". */
const ProviderTarget = z
  .string()
  .max(200)
  .regex(/^[a-z0-9][a-z0-9_-]*\/[^/\s]\S*$/, "provider/model, e.g. anthropic/claude-haiku-5-5");
const ModelAlias = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]*$/);
export const ModelProviderMapPut = z
  .strictObject({
    /** The whole map: aliases left out are removed. At most 200 entries. */
    entries: z
      .array(z.strictObject({ alias: ModelAlias, target: ProviderTarget }))
      .max(200)
      .refine((xs) => new Set(xs.map((x) => x.alias)).size === xs.length, "duplicate alias"),
  })
  .meta({ id: "ModelProviderMapPut" });
export const ModelProviderMap = z
  .object({
    entries: z.array(
      z.object({
        alias: z.string(),
        target: z.string(),
        updated_by_user_id: Uuid.nullable(),
        updated_at: z.string(),
      }),
    ),
    /**
     * Gateway model aliases in use with no provider mapping ("Unmapped model" in the console):
     * never sent to a DE as a guess; a DE on own keys whose runtime alias is one of them is not
     * handed off until it is mapped.
     */
    unmapped: z.array(z.string()),
  })
  .meta({ id: "ModelProviderMap" });
export type ModelProviderMap = z.infer<typeof ModelProviderMap>;

export const MaintenancePlanRow = MaintenancePlan.extend({
  /** Live DE maintenance lines on this version. */
  des: z.int(),
  current: z.boolean(),
}).meta({ id: "MaintenancePlanRow" });

export const TemplateBillingRow = z
  .object({
    template_id: Uuid,
    key: z.string(),
    name: z.string(),
    version: z.int(),
    maintenance_tier: MaintenanceTier.nullable(),
    setup_fee_minor: MinorAmount.nullable(),
  })
  .meta({ id: "TemplateBillingRow" });

export const PriceLists = z
  .object({
    platform: z.array(PlatformPlanRow),
    maintenance: z.array(MaintenancePlanRow),
    catalog: z.array(ChargeCatalogItem),
    templates: z.array(TemplateBillingRow),
    defaults: BillingDefaults,
    /** The caller is a platform admin and may change the lists. */
    can_edit: z.boolean(),
  })
  .meta({ id: "PriceLists" });

export const PlatformPlanCreate = z
  .strictObject({
    key: Slug,
    name: z.string().min(1).max(80),
    interval: BillingInterval.default("month"),
    base_price_minor: Cents,
    included_seats: z.int().min(0).max(10_000),
    extra_seat_price_minor: Cents,
    grace_days: z.int().min(0).max(90),
    effective_from: IsoDate,
    /** BILLING-LEDGER: how the Skillsmith's maintenance runs are billed on this plan. */
    system_usage: SystemUsageBilling.default("billable"),
    /**
     * ONBOARDING-P3 (PLAN-DES): the digital employees the plan includes; creating one past it is
     * refused (`de_allowance_reached`). Omitted or null: no limit.
     */
    included_des: z.int().min(0).max(1000).nullable().optional(),
  })
  .meta({ id: "PlatformPlanCreate" });

export const MaintenancePlanCreate = z
  .strictObject({
    tier: MaintenanceTier,
    name: z.string().min(1).max(80),
    price_minor: Cents,
    ag_cap: z.int().min(0).max(1000),
    supervision: MaintenanceSupervision,
    effective_from: IsoDate,
  })
  .meta({ id: "MaintenancePlanCreate" });

const PlanParams = z.strictObject({ plan_id: Uuid });
const CatalogParams = z.strictObject({ item_id: Uuid });
const TemplateParams = z.strictObject({ template_id: Uuid });

export const ChargeCatalogCreate = z
  .strictObject({
    key: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/),
    name: z.string().min(1).max(120),
    kind: ChargeKind,
    default_price_minor: Cents,
    taxable: z.boolean().default(false),
  })
  .meta({ id: "ChargeCatalogCreate" });

export const ChargeCatalogPatch = z
  .strictObject({
    name: z.string().min(1).max(120).optional(),
    kind: ChargeKind.optional(),
    default_price_minor: Cents.optional(),
    taxable: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .meta({ id: "ChargeCatalogPatch" });

export const BillingDefaultsPut = z
  .strictObject({
    net_terms_days: z.int().min(0).max(120),
    setup_fee_minor: Cents,
    reminder_day: z.int().min(1).max(60),
    payment_methods: z.array(InvoicePaymentMethod).min(1).max(3),
  })
  .meta({ id: "BillingDefaultsPut" });

export const TemplateBillingPut = z
  .strictObject({
    maintenance_tier: MaintenanceTier.nullable(),
    setup_fee_minor: Cents.nullable(),
  })
  .meta({ id: "TemplateBillingPut" });

// Operator: companies ---------------------------------------------------------------------------

export const OperatorBillingCompanyRow = z
  .object({
    company_id: Uuid,
    name: z.string(),
    slug: z.string(),
    plan_key: z.string().nullable(),
    plan_name: z.string().nullable(),
    status: SubscriptionStatus.nullable(),
    collection_method: CollectionMethod,
    people_qty: z.int(),
    des: z.int(),
    /** The next invoice's total as Stripe last previewed it. */
    next_invoice_minor: MinorAmount.nullable(),
    discount_label: z.string().nullable(),
    open_minor: MinorAmount,
    overdue: z.int(),
    grace_until: Timestamp.nullable(),
  })
  .meta({ id: "OperatorBillingCompanyRow" });

export const OperatorBillingOverrides = z
  .object({
    base_price_override_minor: MinorAmount.nullable(),
    extra_seat_price_override_minor: MinorAmount.nullable(),
    included_seats_override: z.int().nullable(),
    maintenance_overrides: MaintenanceOverrides,
    grace_days: z.int().nullable(),
    setup_fee_minor: MinorAmount.nullable(),
    /** The company's own net terms and methods; null: the platform's defaults. */
    net_terms_days: z.int().nullable(),
    payment_methods: z.array(InvoicePaymentMethod).nullable(),
  })
  .meta({ id: "OperatorBillingOverrides" });

/**
 * A billing change still on its way to Stripe (saved, retried automatically), or one Stripe
 * refused lately. Kinds: `subscription.put`, `subscription.sync`, `invoice.create`, ….
 */
export const BillingOperationView = z
  .strictObject({
    id: Uuid,
    kind: z.string().max(60),
    status: z.enum(["queued", "planned", "failed"]),
    attempts: z.int().min(0),
    /** The last error (Stripe's message or the refusal), when there is one. */
    error: z.string().max(500).nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "BillingOperationView" });

export const OperatorCompanyBilling = BillingOverview.extend({
  company_id: Uuid,
  overrides: OperatorBillingOverrides,
  discounts: z.array(DiscountView),
  projects: z.array(ProjectView),
  /** Pending changes first (oldest first), then failures not dismissed (Retry / Dismiss). */
  operations: z.array(BillingOperationView),
}).meta({ id: "OperatorCompanyBilling" });

const OperatorOperationParams = z.strictObject({ company_id: Uuid, operation_id: Uuid });

const OperatorCompanyParams = z.strictObject({ company_id: Uuid });
const OperatorInvoiceParams = z.strictObject({ company_id: Uuid, invoice_id: Uuid });
const OperatorQuoteParams = z.strictObject({ company_id: Uuid, quote_id: Uuid });
const OperatorDiscountParams = z.strictObject({ company_id: Uuid, discount_id: Uuid });
const OperatorDeParams = z.strictObject({ company_id: Uuid, membership_id: Uuid });
const OperatorProjectParams = z.strictObject({ company_id: Uuid, project_id: Uuid });
const OperatorMilestoneParams = z.strictObject({
  company_id: Uuid,
  project_id: Uuid,
  milestone_id: Uuid,
});

export const SubscriptionPut = z
  .strictObject({
    /** The platform plan; its current version applies (an existing company keeps its version
     *  until its next renewal unless `plan_key` changes). */
    plan_key: Slug,
    trial_end: Timestamp.nullable().optional(),
    grace_days: z.int().min(0).max(90).nullable().optional(),
    base_price_override_minor: Cents.nullable().optional(),
    extra_seat_price_override_minor: Cents.nullable().optional(),
    included_seats_override: z.int().min(0).max(10_000).nullable().optional(),
    maintenance_overrides: MaintenanceOverrides.optional(),
  })
  .meta({ id: "SubscriptionPut" });

export const SubscriptionCancel = z
  .strictObject({ at_period_end: z.boolean() })
  .meta({ id: "SubscriptionCancel" });

export const CollectionPut = z
  .strictObject({
    collection_method: CollectionMethod,
    /** null = the platform default. */
    net_terms_days: z.int().min(0).max(120).nullable(),
    payment_methods: z.array(InvoicePaymentMethod).min(1).max(3).nullable(),
    billing_email: z.email().max(320).nullable(),
    setup_fee_minor: Cents.nullable(),
    invoice_topups_allowed: z.boolean(),
  })
  .meta({ id: "CollectionPut" });

export const DiscountCreate = z
  .strictObject({
    label: z.string().min(1).max(60).default("Founding client"),
    percent_bp: Bp,
    months: z.int().min(1).max(36),
    applies_to: DiscountAppliesTo,
    /** Today when omitted; a later day extends a live discount (it starts when that one ends). */
    starts_on: IsoDate.optional(),
  })
  .meta({ id: "DiscountCreate" });

export const MaintenancePut = z
  .strictObject({ tier: MaintenanceTier })
  .meta({ id: "MaintenancePut" });

export const MarkPaid = z
  .strictObject({
    paid_on: IsoDate,
    reference: z.string().min(1).max(200),
  })
  .meta({ id: "MarkPaid" });

export const InvoiceCreate = z
  .strictObject({
    source: z.enum(["one_off", "setup", "maintenance"]).default("one_off"),
    lines: z.array(DraftLine).min(1).max(50),
    memo: z.string().max(2000).nullable().optional(),
    /** Invoice collection only: days to pay; the company's net terms when omitted. */
    days_until_due: z.int().min(0).max(120).nullable().optional(),
    /** Finalise and send now; false keeps a draft. */
    send: z.boolean(),
  })
  .meta({ id: "InvoiceCreate" });

export const QuoteCreate = z
  .strictObject({
    title: z.string().min(1).max(200),
    lines: z.array(DraftLine).min(1).max(50),
    expires_on: IsoDate,
    memo: z.string().max(2000).nullable().optional(),
    project_id: Uuid.nullable().optional(),
    send: z.boolean(),
  })
  .meta({ id: "QuoteCreate" });

export const MilestoneDraft = z
  .strictObject({
    name: z.string().min(1).max(200),
    amount_minor: Cents.min(1),
    due_on: IsoDate.nullable(),
  })
  .meta({ id: "MilestoneDraft" });

export const ProjectCreate = z
  .strictObject({
    name: z.string().min(1).max(200),
    kind: BuildProjectKind,
    total_minor: Cents,
    quote_id: Uuid.nullable().optional(),
    milestones: z.array(MilestoneDraft).min(1).max(50),
    maintenance_shape: BuildMaintenanceShape,
    maintenance_pct_bp: Bp.nullable().optional(),
    retainer_minor: Cents.min(1).nullable().optional(),
  })
  .meta({ id: "ProjectCreate" });

export const ProjectPatch = z
  .strictObject({
    status: BuildProjectStatus.optional(),
    delivered_on: IsoDate.nullable().optional(),
    maintenance_shape: BuildMaintenanceShape.optional(),
    maintenance_pct_bp: Bp.nullable().optional(),
    retainer_minor: Cents.min(1).nullable().optional(),
  })
  .meta({ id: "ProjectPatch" });

export const SendFlag = z.strictObject({ send: z.boolean() }).meta({ id: "SendFlag" });

export const OperatorInvoiceQuery = PageQuery.extend({
  status: InvoiceDisplayStatus.optional(),
  company: Uuid.optional(),
});

export const OperatorInvoiceRow = InvoiceView.extend({
  company_name: z.string(),
  company_slug: z.string(),
}).meta({ id: "OperatorInvoiceRow" });

export const OperatorQuoteRow = Quote.extend({
  company_name: z.string(),
  company_slug: z.string(),
}).meta({ id: "OperatorQuoteRow" });

export const InvoicesAndQuotes = z
  .object({
    invoices: z.array(OperatorInvoiceRow),
    quotes: z.array(OperatorQuoteRow),
    /** Companies whose rows could not be read (a grant changed mid-read); never silent. */
    skipped: z.int(),
    /** Pass as `cursor` for the next (older) page; null on the last. Quotes come on page one only. */
    next_cursor: z.string().nullable(),
  })
  .meta({ id: "InvoicesAndQuotes" });

// Routes ------------------------------------------------------------------------------------------

// BILLING-LEDGER: the payment provider of a company (Stripe, or the ledger) ---------------------

export const PaymentProviderKind = z.enum(["stripe", "ledger"]).meta({ id: "PaymentProviderKind" });

export const PaymentProviderView = z
  .object({
    company_id: Uuid,
    /** Who takes the company's money now (null: nothing is configured to bill). */
    provider: PaymentProviderKind.nullable(),
    /** The operator's choice on record (null: the default rule). */
    selected: PaymentProviderKind.nullable(),
    stripe_configured: z.boolean(),
    ledger_enabled: z.boolean(),
    /** The ledger's side of the company (null when it has no ledger state). */
    ledger: z
      .object({
        has_customer: z.boolean(),
        subscription_status: z.string().nullable(),
        current_period_end: Timestamp.nullable(),
        next_renewal_at: Timestamp.nullable(),
        open_invoices: z.int().min(0),
        unpaid_top_ups: z.int().min(0),
        undelivered_events: z.int().min(0),
      })
      .nullable(),
    /** How the company's system usage (Skillsmith maintenance runs) is billed now. */
    system_usage: SystemUsageBilling,
    /** The company's own setting (null: its plan's). */
    system_usage_override: SystemUsageBilling.nullable(),
    /** lane D: whether voice usage is charged to the wallet (VOICE-CHOICES: default on). */
    bill_voice_usage: z.boolean(),
    /** VOICE-CHOICES: premium voices bill provider cost × this (basis points; 10 000 = 1.0). */
    voice_markup_bp: z.int().min(0),
    /** VOICE-CHOICES: what a minute of a free (self-hosted) voice bills, in micros (default 0). */
    voice_free_rate_micros: z.int().min(0),
    /** VOICE-CHOICES: what a minute of a self-hosted GPU voice bills, in micros (default 3¢). */
    voice_self_hosted_rate_micros: z.int().min(0),
  })
  .meta({ id: "PaymentProviderView" });

export const PaymentProviderPut = z
  .strictObject({
    /** `stripe` from the ledger moves the company (customer, subscription, discounts). */
    provider: PaymentProviderKind.optional(),
    /** The company's system usage billing (null: back to its plan's). */
    system_usage: SystemUsageBilling.nullable().optional(),
    /** lane D: charge voice usage (STT, TTS, the voice LLM) to the wallet. */
    bill_voice_usage: z.boolean().optional(),
    /** VOICE-CHOICES: the premium voice markup (basis points, 10 000 = at cost). */
    voice_markup_bp: z.int().min(0).max(100_000).optional(),
    /** VOICE-CHOICES: the free voices' compute rate per minute, in micros (0: included). */
    voice_free_rate_micros: z.int().min(0).max(10_000_000).optional(),
    /** VOICE-CHOICES: the self-hosted (GPU) voices' rate per minute, in micros. */
    voice_self_hosted_rate_micros: z.int().min(0).max(10_000_000).optional(),
  })
  .refine(
    (b) =>
      b.provider !== undefined ||
      b.system_usage !== undefined ||
      b.bill_voice_usage !== undefined ||
      b.voice_markup_bp !== undefined ||
      b.voice_free_rate_micros !== undefined ||
      b.voice_self_hosted_rate_micros !== undefined,
    { message: "provider, system_usage, bill_voice_usage or a voice rate is required" },
  )
  .meta({ id: "PaymentProviderPut" });

// lane D: the month-end close worksheet (gap 8; shared contracts §4.3) -----------------------------
export const ClosePeriodParams = z.strictObject({
  company_id: Uuid,
  period_key: z
    .string()
    .max(7)
    .regex(/^\d{4}-\d{2}$/),
});
const CloseLine = z
  .strictObject({
    cost_event_id: Uuid,
    occurred_at: Timestamp,
    created_at: Timestamp,
    kind: z.string().max(40),
    usage_category: z.string().max(40),
    request_id: Uuid.nullable(),
    run_id: Uuid.nullable(),
    parent_run_id: Uuid.nullable(),
    meter_identity: z.string().max(500),
    cost_micros: z.int(),
    wallet_entry_id: Uuid.nullable(),
    wallet_micros: z.int(),
    status: z.enum(["charged", "included", "run_charged", "not_charged", "unresolved", "reversal"]),
    late: z.boolean(),
  })
  .meta({ id: "CloseWorksheetLine" });
const CategoryTotals = z.record(
  z.string(),
  z.strictObject({ cost_micros: z.int(), wallet_micros: z.int() }),
);
export const PeriodClose = z
  .strictObject({
    closed_at: Timestamp,
    by_user_id: Uuid.nullable(),
    totals_by_category: CategoryTotals,
    unresolved_count: z.int().min(0),
    late_count: z.int().min(0),
    invoice_total_minor: z.int(),
    worksheet_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .meta({ id: "PeriodClose" });
export const CloseWorksheet = z
  .strictObject({
    company_id: Uuid,
    period_key: z.string(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    requests: z.array(
      z.strictObject({
        request_id: Uuid,
        origin: z.string().nullable(),
        state: z.string().nullable(),
        runs: z.int().min(0),
        helper_runs: z.int().min(0),
        cost_micros: z.int(),
        wallet_micros: z.int(),
      }),
    ),
    lines: z.array(CloseLine),
    totals_by_category: CategoryTotals,
    wallet: z.strictObject({
      opening_micros: z.int(),
      topups_micros: z.int(),
      bonus_micros: z.int(),
      usage_micros: z.int(),
      refunds_micros: z.int(),
      adjustments_micros: z.int(),
      reversals_micros: z.int(),
      closing_micros: z.int(),
      usage_by_category: z.record(
        z.string(),
        z.strictObject({ micros: z.int(), lines: z.int().min(0) }),
      ),
    }),
    invoices: z.array(
      z.strictObject({
        invoice_id: Uuid,
        source: z.string(),
        status: z.string(),
        total_minor: z.int(),
        topup_micros: z.int().nullable(),
      }),
    ),
    unresolved: z.array(CloseLine),
    late_adjustments: z.array(
      z.strictObject({
        kind: z.enum(["cost", "wallet"]),
        id: Uuid,
        occurred_at: Timestamp,
        created_at: Timestamp,
        micros: z.int(),
        usage_category: z.string().nullable(),
      }),
    ),
    reconciliation: z.strictObject({
      invoice_total_minor: z.int(),
      invoices_by_source: z.record(z.string(), z.int()),
      topup_invoiced_micros: z.int(),
      wallet_topups_micros: z.int(),
      usage_invoice_lines: z.int().min(0),
      wallet_usage_micros: z.int(),
      ok: z.boolean(),
      problems: z.array(z.string()),
    }),
    closed: PeriodClose.nullable(),
  })
  .meta({ id: "CloseWorksheet" });

export const billingRoutes = {
  // Client --------------------------------------------------------------------------------------
  "billing.overview": defineRoute({
    ...billing,
    id: "billing.overview",
    method: "get",
    path: `${base}/billing/subscription`,
    summary: "The plan: status, people, agents and tiers, the next invoice, how the company pays",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: BillingOverview },
    errors: [],
    emits: [],
  }),
  "billing.invoices.list": defineRoute({
    ...billing,
    id: "billing.invoices.list",
    method: "get",
    path: `${base}/billing/invoices`,
    summary: "Invoices of every kind, newest first (drafts are the operator's)",
    access: "manager",
    params: CompanyParams,
    query: InvoiceListQuery,
    response: { status: 200, schema: page(InvoiceView) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "billing.invoices.get": defineRoute({
    ...billing,
    id: "billing.invoices.get",
    method: "get",
    path: `${base}/billing/invoices/{invoice_id}`,
    summary: "One invoice with its lines",
    access: "manager",
    params: InvoiceParams,
    response: { status: 200, schema: InvoiceView },
    errors: [],
    emits: [],
  }),
  "billing.invoices.pdf": defineRoute({
    ...billing,
    id: "billing.invoices.pdf",
    method: "get",
    path: `${base}/billing/invoices/{invoice_id}/pdf`,
    summary: "A ledger invoice's PDF (BILLING-LEDGER; a Stripe invoice links Stripe's own)",
    access: "manager",
    params: InvoiceParams,
    response: { status: 200, contentType: "application/pdf" },
    errors: [],
    emits: [],
  }),
  "billing.portal": defineRoute({
    ...billing,
    id: "billing.portal",
    method: "post",
    path: `${base}/billing/portal`,
    summary: "A Stripe Billing Portal session: card or bank account, receipts (owner or admin)",
    access: "manager",
    // A Stripe session URL only: nothing changes in CompanyOS until Stripe reports it.
    safe: true,
    params: CompanyParams,
    body: PortalCreate,
    response: { status: 200, schema: PortalSession },
    errors: ["billing_not_configured", "payment_provider_error"],
    emits: [],
  }),
  "billing.quotes.list": defineRoute({
    ...billing,
    id: "billing.quotes.list",
    method: "get",
    path: `${base}/billing/quotes`,
    summary: "Quotes sent to the company (drafts are the operator's)",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Quote) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "billing.quotes.pdf": defineRoute({
    ...billing,
    id: "billing.quotes.pdf",
    method: "get",
    path: `${base}/billing/quotes/{quote_id}/pdf`,
    summary: "A sent quote's PDF, as Stripe renders it",
    access: "manager",
    params: QuoteParams,
    response: { status: 200, contentType: "application/pdf" },
    errors: ["billing_not_configured", "payment_provider_error"],
    emits: [],
  }),
  "billing.quotes.accept": defineRoute({
    ...billing,
    id: "billing.quotes.accept",
    method: "post",
    path: `${base}/billing/quotes/{quote_id}/accept`,
    summary: "Accept a sent quote (owner or admin); its invoice follows",
    access: "manager",
    params: QuoteParams,
    idempotency: "optional",
    response: { status: 200, schema: Quote },
    errors: ["conflict", "payment_provider_error"],
    emits: [
      "quote.changed",
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "project.changed",
    ],
  }),
  "billing.projects.list": defineRoute({
    ...billing,
    id: "billing.projects.list",
    method: "get",
    path: `${base}/billing/projects`,
    summary: "Build projects and their milestones with each one's invoice",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(ProjectView) }) },
    errors: [],
    emits: [],
  }),
  "billing.topupInvoices.create": defineRoute({
    ...billing,
    id: "billing.topupInvoices.create",
    method: "post",
    path: `${base}/billing/topup-invoices`,
    summary: "Buy credit by invoice (owner or admin): it lands, with its bonus, when paid",
    access: "manager",
    params: CompanyParams,
    body: TopupInvoiceCreate,
    idempotency: "optional",
    response: { status: 201, schema: InvoiceView },
    // conflict: credit by invoice is not on until the wallet credits a paid one
    errors: ["billing_not_configured", "wallet_off", "conflict", "payment_provider_error"],
    emits: [
      "wallet.topup_invoiced",
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
    ],
  }),

  // Operator: price lists --------------------------------------------------------------------------
  "operator.billing.prices": defineRoute({
    ...billing,
    id: "operator.billing.prices",
    method: "get",
    path: `${op}/prices`,
    summary: "Plans, maintenance tiers, the charge catalog, defaults and template fees",
    access: "operator",
    response: { status: 200, schema: PriceLists },
    errors: [],
    emits: [],
  }),
  "operator.billing.plans.create": defineRoute({
    ...billing,
    id: "operator.billing.plans.create",
    method: "post",
    path: `${op}/plans`,
    summary: "A new platform plan version (its Stripe prices are created with it)",
    access: "operator",
    body: PlatformPlanCreate,
    idempotency: "optional",
    response: { status: 201, schema: PlatformPlanRow },
    errors: ["conflict", "payment_provider_error"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.planModelAccess.put": defineRoute({
    ...billing,
    id: "operator.billing.planModelAccess.put",
    method: "put",
    path: `${op}/plans/by-key/{plan_key}/model-access`,
    summary: "Allow or refuse own model keys for every company on a plan (audited)",
    access: "operator",
    params: PlanKeyParams,
    body: PlanModelAccessPut,
    response: { status: 200, schema: PlanModelAccess },
    errors: ["not_found", "forbidden"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.modelProviderMap.get": defineRoute({
    ...billing,
    id: "operator.billing.modelProviderMap.get",
    method: "get",
    path: `${op}/model-provider-map`,
    summary: "Which provider (and its model id) each gateway model alias needs a company key for",
    access: "operator",
    response: { status: 200, schema: ModelProviderMap },
    errors: ["forbidden"],
    emits: [],
  }),
  "operator.billing.modelProviderMap.put": defineRoute({
    ...billing,
    id: "operator.billing.modelProviderMap.put",
    method: "put",
    path: `${op}/model-provider-map`,
    summary: "Replace the model alias → provider map (audited per alias added, changed or removed)",
    access: "operator",
    body: ModelProviderMapPut,
    response: { status: 200, schema: ModelProviderMap },
    errors: ["forbidden"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.plans.retire": defineRoute({
    ...billing,
    id: "operator.billing.plans.retire",
    method: "delete",
    path: `${op}/plans/{plan_id}`,
    summary: "Retire a platform plan version no company is on",
    access: "operator",
    params: PlanParams,
    response: { status: 200, schema: PlatformPlanRow },
    errors: ["conflict"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.maintenancePlans.create": defineRoute({
    ...billing,
    id: "operator.billing.maintenancePlans.create",
    method: "post",
    path: `${op}/maintenance-plans`,
    summary: "A new maintenance tier version (its Stripe price is created with it)",
    access: "operator",
    body: MaintenancePlanCreate,
    idempotency: "optional",
    response: { status: 201, schema: MaintenancePlanRow },
    errors: ["conflict", "payment_provider_error"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.maintenancePlans.retire": defineRoute({
    ...billing,
    id: "operator.billing.maintenancePlans.retire",
    method: "delete",
    path: `${op}/maintenance-plans/{plan_id}`,
    summary: "Retire a maintenance tier version no DE is on",
    access: "operator",
    params: PlanParams,
    response: { status: 200, schema: MaintenancePlanRow },
    errors: ["conflict"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.catalog.create": defineRoute({
    ...billing,
    id: "operator.billing.catalog.create",
    method: "post",
    path: `${op}/charge-catalog`,
    summary: "Add a one-off line to the charge catalog",
    access: "operator",
    body: ChargeCatalogCreate,
    idempotency: "optional",
    response: { status: 201, schema: ChargeCatalogItem },
    errors: ["conflict"],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.catalog.update": defineRoute({
    ...billing,
    id: "operator.billing.catalog.update",
    method: "patch",
    path: `${op}/charge-catalog/{item_id}`,
    summary: "Edit a charge catalog line",
    access: "operator",
    params: CatalogParams,
    body: ChargeCatalogPatch,
    response: { status: 200, schema: ChargeCatalogItem },
    errors: [],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.catalog.delete": defineRoute({
    ...billing,
    id: "operator.billing.catalog.delete",
    method: "delete",
    path: `${op}/charge-catalog/{item_id}`,
    summary: "Remove a charge catalog line (quotes and invoices keep their copies)",
    access: "operator",
    params: CatalogParams,
    response: { status: 200, schema: ChargeCatalogItem },
    errors: [],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.defaults.put": defineRoute({
    ...billing,
    id: "operator.billing.defaults.put",
    method: "put",
    path: `${op}/defaults`,
    summary: "Platform defaults: net terms, template setup fee, reminder day, payment methods",
    access: "operator",
    body: BillingDefaultsPut,
    response: { status: 200, schema: BillingDefaults },
    errors: [],
    emits: ["billing.price_changed"],
  }),
  "operator.billing.templates.put": defineRoute({
    ...billing,
    id: "operator.billing.templates.put",
    method: "put",
    path: `${op}/templates/{template_id}`,
    summary: "A template's default maintenance tier and setup fee",
    access: "operator",
    params: TemplateParams,
    body: TemplateBillingPut,
    response: { status: 200, schema: TemplateBillingRow },
    errors: [],
    emits: ["billing.price_changed"],
  }),

  // Operator: across companies --------------------------------------------------------------------
  "operator.billing.companies": defineRoute({
    ...billing,
    id: "operator.billing.companies",
    method: "get",
    path: `${op}/companies`,
    summary: "Every granted company's plan, status, collection and what it owes",
    access: "operator",
    response: { status: 200, schema: z.object({ data: z.array(OperatorBillingCompanyRow) }) },
    errors: [],
    emits: [],
  }),
  "operator.billing.invoices.list": defineRoute({
    ...billing,
    id: "operator.billing.invoices.list",
    method: "get",
    path: `${op}/invoices`,
    summary: "Invoices and quotes across granted companies, newest first",
    access: "operator",
    query: OperatorInvoiceQuery,
    response: { status: 200, schema: InvoicesAndQuotes },
    errors: [],
    emits: [],
  }),

  // Operator: one company -------------------------------------------------------------------------
  "operator.billing.company": defineRoute({
    ...billing,
    id: "operator.billing.company",
    method: "get",
    path: `${op}/companies/{company_id}`,
    summary: "One company's billing: plan, overrides, discounts, agents and tiers, projects",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: [],
    emits: [],
  }),
  "operator.billing.subscription.put": defineRoute({
    ...billing,
    id: "operator.billing.subscription.put",
    method: "put",
    path: `${op}/companies/{company_id}/subscription`,
    summary: "Put the company on a plan, or change its prices, trial or grace (one subscription)",
    access: "operator",
    params: OperatorCompanyParams,
    body: SubscriptionPut,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict", "billing_not_configured", "payment_provider_error"],
    emits: [
      "subscription.created",
      "subscription.updated",
      "subscription.quantity_changed",
      "de_maintenance.changed",
      // a first subscription's first invoice, or a mid-period increase's, mirrored as Stripe made
      // and collected it
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
      "subscription.past_due",
      "cost.posted",
    ],
  }),
  "operator.billing.subscription.retry": defineRoute({
    ...billing,
    id: "operator.billing.subscription.retry",
    method: "post",
    path: `${op}/companies/{company_id}/subscription/retry`,
    summary: "Retry the payment of a past-due subscription's open invoice now",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict", "payment_provider_error"],
    emits: [
      "invoice.paid",
      "invoice.payment_failed",
      "subscription.restored",
      "digital_employee.resumed",
      "cost.posted",
    ],
  }),
  "operator.billing.subscription.cancel": defineRoute({
    ...billing,
    id: "operator.billing.subscription.cancel",
    method: "post",
    path: `${op}/companies/{company_id}/subscription/cancel`,
    summary: "Cancel the subscription now or at the end of the period",
    access: "operator",
    params: OperatorCompanyParams,
    body: SubscriptionCancel,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict", "payment_provider_error"],
    emits: [
      "subscription.cancelled",
      "subscription.restored",
      "digital_employee.resumed",
      "de_maintenance.changed",
    ],
  }),
  "operator.billing.collection.put": defineRoute({
    ...billing,
    id: "operator.billing.collection.put",
    method: "put",
    path: `${op}/companies/{company_id}/collection`,
    summary: "Card or invoice collection, net terms, payment methods, setup fee override",
    access: "operator",
    params: OperatorCompanyParams,
    body: CollectionPut,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["payment_provider_error"],
    emits: ["billing.account_changed"],
  }),
  "operator.billing.discounts.create": defineRoute({
    ...billing,
    id: "operator.billing.discounts.create",
    method: "post",
    path: `${op}/companies/{company_id}/discounts`,
    summary: "Grant a founding-client discount: a percentage off for N months",
    access: "operator",
    params: OperatorCompanyParams,
    body: DiscountCreate,
    idempotency: "optional",
    response: { status: 201, schema: DiscountView },
    errors: ["conflict", "payment_provider_error"],
    emits: ["discount.granted", "subscription.updated"],
  }),
  "operator.billing.discounts.end": defineRoute({
    ...billing,
    id: "operator.billing.discounts.end",
    method: "post",
    path: `${op}/companies/{company_id}/discounts/{discount_id}/end`,
    summary: "End a discount early (removed at the next renewal, never mid-period)",
    access: "operator",
    params: OperatorDiscountParams,
    response: { status: 200, schema: DiscountView },
    errors: ["conflict"],
    emits: ["discount.ended"],
  }),
  "operator.billing.operations.retry": defineRoute({
    ...billing,
    id: "operator.billing.operations.retry",
    method: "post",
    path: `${op}/companies/{company_id}/operations/{operation_id}/retry`,
    summary:
      "Run a failed billing change again (what it made at Stripe is found by its id, never twice)",
    access: "operator",
    params: OperatorOperationParams,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict"],
    emits: ["billing_operation.retried"],
  }),
  "operator.billing.operations.dismiss": defineRoute({
    ...billing,
    id: "operator.billing.operations.dismiss",
    method: "post",
    path: `${op}/companies/{company_id}/operations/{operation_id}/dismiss`,
    summary: "Dismiss a failed billing change (no longer listed; its alert is closed)",
    access: "operator",
    params: OperatorOperationParams,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict"],
    emits: ["billing_operation.dismissed"],
  }),
  "operator.billing.maintenance.put": defineRoute({
    ...billing,
    id: "operator.billing.maintenance.put",
    method: "put",
    path: `${op}/companies/{company_id}/digital-employees/{membership_id}/maintenance`,
    summary:
      "Change a DE's maintenance tier (an upgrade invoiced prorated at once; a downgrade at the renewal)",
    access: "operator",
    params: OperatorDeParams,
    body: MaintenancePut,
    response: { status: 200, schema: BillingAgentLine },
    errors: ["conflict", "billing_not_configured", "payment_provider_error"],
    emits: [
      "de_maintenance.changed",
      "subscription.quantity_changed",
      // an upgrade's prorated increase, invoiced and collected at once
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
      "subscription.past_due",
      "cost.posted",
    ],
  }),
  "operator.billing.invoices.create": defineRoute({
    ...billing,
    id: "operator.billing.invoices.create",
    method: "post",
    path: `${op}/companies/{company_id}/invoices`,
    summary: "A one-off invoice from catalog or custom lines; a draft, or sent at once",
    access: "operator",
    params: OperatorCompanyParams,
    body: InvoiceCreate,
    idempotency: "optional",
    response: { status: 201, schema: InvoiceView },
    errors: ["billing_not_configured", "payment_provider_error"],
    emits: ["invoice.created", "invoice.finalized", "invoice.paid", "invoice.payment_failed"],
  }),
  "operator.billing.invoices.send": defineRoute({
    ...billing,
    id: "operator.billing.invoices.send",
    method: "post",
    path: `${op}/companies/{company_id}/invoices/{invoice_id}/send`,
    summary: "Finalise and send a draft invoice (or charge the card on file)",
    access: "operator",
    params: OperatorInvoiceParams,
    response: { status: 200, schema: InvoiceView },
    errors: ["conflict", "payment_provider_error"],
    emits: ["invoice.sent", "invoice.finalized", "invoice.paid", "invoice.payment_failed"],
  }),
  "operator.billing.invoices.remind": defineRoute({
    ...billing,
    id: "operator.billing.invoices.remind",
    method: "post",
    path: `${op}/companies/{company_id}/invoices/{invoice_id}/remind`,
    summary: "Send a payment reminder for an open invoice",
    access: "operator",
    params: OperatorInvoiceParams,
    response: { status: 200, schema: InvoiceView },
    errors: ["conflict", "payment_provider_error"],
    emits: ["invoice.reminded"],
  }),
  // BILLING-LEDGER --------------------------------------------------------------------------------
  "operator.billing.provider.get": defineRoute({
    ...billing,
    id: "operator.billing.provider.get",
    method: "get",
    path: `${op}/companies/{company_id}/provider`,
    summary: "Who takes the company's money (Stripe or the ledger) and the ledger's open items",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: PaymentProviderView },
    errors: [],
    emits: [],
  }),
  "operator.billing.provider.put": defineRoute({
    ...billing,
    id: "operator.billing.provider.put",
    method: "put",
    path: `${op}/companies/{company_id}/provider`,
    summary:
      "Put the company on the ledger or on Stripe; from the ledger to Stripe it moves the customer, the subscription and its discounts",
    access: "operator",
    params: OperatorCompanyParams,
    body: PaymentProviderPut,
    response: { status: 200, schema: PaymentProviderView },
    errors: ["conflict", "billing_not_configured", "payment_provider_error"],
    emits: ["billing.account_changed"],
  }),
  // lane D: month-end close -------------------------------------------------------------------
  "operator.billing.close.get": defineRoute({
    ...billing,
    id: "operator.billing.close.get",
    method: "get",
    path: `${op}/companies/{company_id}/close/{period_key}`,
    summary:
      "The month-end close worksheet: requests, runs and helpers, metered usage, wallet movements and invoices, with unresolved usage and late adjustments",
    access: "operator",
    params: ClosePeriodParams,
    response: { status: 200, schema: CloseWorksheet },
    errors: [],
    emits: [],
  }),
  "operator.billing.close.csv": defineRoute({
    ...billing,
    id: "operator.billing.close.csv",
    method: "get",
    path: `${op}/companies/{company_id}/close/{period_key}/csv`,
    summary: "The month-end close worksheet as CSV (one row per cost row, then the totals)",
    access: "operator",
    params: ClosePeriodParams,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: [],
    emits: [],
  }),
  "operator.billing.close.record": defineRoute({
    ...billing,
    id: "operator.billing.close.record",
    method: "post",
    path: `${op}/companies/{company_id}/close/{period_key}`,
    summary:
      "Close an ended month: its totals, unresolved count and worksheet fingerprint are recorded in the ledger state (once)",
    access: "operator",
    params: ClosePeriodParams,
    response: { status: 200, schema: CloseWorksheet },
    errors: ["conflict"],
    emits: ["billing.period_closed"],
  }),
  "operator.billing.ledger.failPayment": defineRoute({
    ...billing,
    id: "operator.billing.ledger.failPayment",
    method: "post",
    path: `${op}/companies/{company_id}/invoices/{invoice_id}/record-failed-payment`,
    summary:
      "A ledger invoice's payment failed (a returned transfer, a declined card): dunning starts as for Stripe",
    access: "operator",
    params: OperatorInvoiceParams,
    response: { status: 200, schema: InvoiceView },
    errors: ["conflict"],
    emits: ["invoice.payment_failed", "subscription.past_due"],
  }),
  "operator.billing.ledger.renewNow": defineRoute({
    ...billing,
    id: "operator.billing.ledger.renewNow",
    method: "post",
    path: `${op}/companies/{company_id}/subscription/renew-now`,
    summary: "A ledger subscription's period ends now and the next one is invoiced",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OperatorCompanyBilling },
    errors: ["conflict"],
    emits: ["invoice.created", "invoice.finalized", "subscription.updated"],
  }),
  "operator.billing.invoices.markPaid": defineRoute({
    ...billing,
    id: "operator.billing.invoices.markPaid",
    method: "post",
    path: `${op}/companies/{company_id}/invoices/{invoice_id}/mark-paid`,
    summary:
      "Mark an invoice paid out of band (a wire to the operator's own bank), with a reference",
    access: "operator",
    params: OperatorInvoiceParams,
    body: MarkPaid,
    response: { status: 200, schema: InvoiceView },
    errors: ["conflict", "payment_provider_error"],
    emits: [
      "invoice.marked_paid",
      "invoice.paid",
      "subscription.restored",
      "digital_employee.resumed",
      "cost.posted",
    ],
  }),
  "operator.billing.invoices.void": defineRoute({
    ...billing,
    id: "operator.billing.invoices.void",
    method: "post",
    path: `${op}/companies/{company_id}/invoices/{invoice_id}/void`,
    summary: "Void an open or draft invoice (nothing is owed; a milestone on it is pending again)",
    access: "operator",
    params: OperatorInvoiceParams,
    response: { status: 200, schema: InvoiceView },
    errors: ["conflict", "payment_provider_error"],
    emits: ["invoice.voided", "subscription.restored", "digital_employee.resumed"],
  }),
  "operator.billing.quotes.list": defineRoute({
    ...billing,
    id: "operator.billing.quotes.list",
    method: "get",
    path: `${op}/companies/{company_id}/quotes`,
    summary: "The company's quotes, drafts included",
    access: "operator",
    params: OperatorCompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(Quote) }) },
    errors: [],
    emits: [],
  }),
  "operator.billing.quotes.create": defineRoute({
    ...billing,
    id: "operator.billing.quotes.create",
    method: "post",
    path: `${op}/companies/{company_id}/quotes`,
    summary: "Draft a quote (and send it at once when asked)",
    access: "operator",
    params: OperatorCompanyParams,
    body: QuoteCreate,
    idempotency: "optional",
    response: { status: 201, schema: Quote },
    errors: ["billing_not_configured", "payment_provider_error"],
    emits: ["quote.changed"],
  }),
  "operator.billing.quotes.pdf": defineRoute({
    ...billing,
    id: "operator.billing.quotes.pdf",
    method: "get",
    path: `${op}/companies/{company_id}/quotes/{quote_id}/pdf`,
    summary: "A quote's PDF, as Stripe renders it",
    access: "operator",
    params: OperatorQuoteParams,
    response: { status: 200, contentType: "application/pdf" },
    errors: ["billing_not_configured", "payment_provider_error"],
    emits: [],
  }),
  "operator.billing.quotes.send": defineRoute({
    ...billing,
    id: "operator.billing.quotes.send",
    method: "post",
    path: `${op}/companies/{company_id}/quotes/{quote_id}/send`,
    summary: "Finalise and send a draft quote",
    access: "operator",
    params: OperatorQuoteParams,
    response: { status: 200, schema: Quote },
    errors: ["conflict", "payment_provider_error"],
    emits: ["quote.changed"],
  }),
  "operator.billing.quotes.cancel": defineRoute({
    ...billing,
    id: "operator.billing.quotes.cancel",
    method: "post",
    path: `${op}/companies/{company_id}/quotes/{quote_id}/cancel`,
    summary: "Cancel a draft or sent quote",
    access: "operator",
    params: OperatorQuoteParams,
    response: { status: 200, schema: Quote },
    errors: ["conflict", "payment_provider_error"],
    emits: ["quote.changed"],
  }),
  "operator.billing.projects.create": defineRoute({
    ...billing,
    id: "operator.billing.projects.create",
    method: "post",
    path: `${op}/companies/{company_id}/projects`,
    summary: "A custom or complex build with its milestones and maintenance shape",
    access: "operator",
    params: OperatorCompanyParams,
    body: ProjectCreate,
    idempotency: "optional",
    response: { status: 201, schema: ProjectView },
    errors: [],
    emits: ["project.changed"],
  }),
  "operator.billing.projects.update": defineRoute({
    ...billing,
    id: "operator.billing.projects.update",
    method: "patch",
    path: `${op}/companies/{company_id}/projects/{project_id}`,
    summary: "A build's status, delivery date or maintenance shape (a retainer rides the plan)",
    access: "operator",
    params: OperatorProjectParams,
    body: ProjectPatch,
    response: { status: 200, schema: ProjectView },
    errors: ["conflict", "billing_not_configured", "payment_provider_error"],
    emits: ["project.changed"],
  }),
  "operator.billing.milestones.invoice": defineRoute({
    ...billing,
    id: "operator.billing.milestones.invoice",
    method: "post",
    path: `${op}/companies/{company_id}/projects/{project_id}/milestones/{milestone_id}/invoice`,
    summary: "Invoice a reached milestone (sent at once, or kept as a draft)",
    access: "operator",
    params: OperatorMilestoneParams,
    body: SendFlag,
    idempotency: "optional",
    response: { status: 201, schema: InvoiceView },
    errors: ["conflict", "billing_not_configured", "payment_provider_error"],
    emits: [
      "project.milestone_invoiced",
      "invoice.created",
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
      "project.changed",
    ],
  }),
} as const;
